// src/contexts/onboarding-context.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useUser } from "@clerk/clerk-react";
import { useLocation } from "react-router-dom";

// 1. Define the shape of the context data
export type OnboardingStage = "START" | "ANALYSIS" | "CHOOSE" | "RESULTS";

interface OnboardingContextType {
  stage: OnboardingStage;
  setStage: (stage: OnboardingStage) => void;
  storyMode: boolean;
  setStoryMode: (mode: boolean) => void;
  webAppUrl: string | null;
  setWebAppUrl: (url: string) => void;
}

// 2. Create the context with a default value
// Exported for dev tools that need to check if context exists without throwing
export const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined,
);

// 3. Create a provider component
interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const { user, isLoaded } = useUser();
  const [stage, setStage] = useState<OnboardingStage>("START");
  const [storyMode, setStoryMode] = useState<boolean>(true); // Default to true for development
  const [webAppUrl, setWebAppUrl] = useState<string | null>(null);
  const location = useLocation();

  // Sync stage from Clerk metadata on mount or user change
  useEffect(() => {
    if (!isLoaded) return;
    const metaStage = user?.unsafeMetadata?.onboardingStage as
      | OnboardingStage
      | undefined;
    if (metaStage && metaStage !== stage) {
      setStage(metaStage);
    }
  }, [isLoaded, user, stage]);

  // This effect syncs the stage with the URL path
  useEffect(() => {
    const path = location.pathname.split("/").pop()?.toUpperCase();
    if (path && ["START", "ANALYSIS", "CHOOSE", "RESULTS"].includes(path)) {
      // A bit of a hack to map results -> choose for stage logic
      const currentStage = (
        path === "RESULTS" ? "CHOOSE" : path
      ) as OnboardingStage;
      if (stage !== currentStage) {
        setStage(currentStage);
      }
    }
  }, [location.pathname, stage]);

  // This effect syncs the last visited onboarding step to localStorage for fast redirects
  useEffect(() => {
    if (location.pathname.startsWith("/onboarding/")) {
      try {
        localStorage.setItem("onboarding_last_step", location.pathname);
      } catch (e) {
        console.warn("Failed to write to localStorage", e);
      }
    }
  }, [location.pathname]);

  // Persist stage changes back to Clerk and localStorage
  useEffect(() => {
    // Store onboarding progress in localStorage for potential transfer
    const onboardingProgress = {
      stage,
      lastVisited: window.location.pathname,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(
      "onboarding_progress",
      JSON.stringify(onboardingProgress),
    );

    // If user is signed in, also update Clerk metadata
    if (!isLoaded || !user) return;
    const metaStage = user.unsafeMetadata?.onboardingStage as
      | OnboardingStage
      | undefined;
    if (metaStage !== stage) {
      user
        .update({
          unsafeMetadata: { ...user.unsafeMetadata, onboardingStage: stage },
        })
        .catch(() => {});
    }
  }, [stage, isLoaded, user]);

  const value = {
    stage,
    setStage,
    storyMode,
    setStoryMode,
    webAppUrl,
    setWebAppUrl,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

// 4. Create a custom hook for easy consumption
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
};
